import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { IngredientLite } from '../../../../../../../shared/models/ingredient/ingredient-lite.model';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { RecipeService } from '../../../../../../../services/recipe/recipe.service';
import { NotificationService } from '../../../../../../../services/notification.service';
import { DataRefsByKey, IngredientType, MarketingFood, Unit } from '../../../../../../../shared/models/dataref.model';
import { Observable, of, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { RefDataService } from '../../../../../../../services/ref-data.service';
import { CdkDragSortEvent } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-variant-step-ingredients',
  templateUrl: './variant-step-ingredients.component.html',
  styleUrls: ['./variant-step-ingredients.component.scss'],
  standalone: false,
})
export class VariantStepIngredientsComponent implements OnChanges, OnDestroy, OnInit {
  @Input() ingredients?: IngredientLite[];

  /**
   * Form array to store ingredients. Must be empty, will be initialized by the component.
   */
  @Input() formIngredients?: UntypedFormArray;
  @Input() yieldUnits?: DataRefsByKey<Unit> | null;
  @Input() domain = '';
  @Input() market = '';
  @Input() lang = '';
  @Input() marketingFoods: MarketingFood[] = [];
  @Input() isAutoControl?: AbstractControl;
  @Input() showAutoBtn = false;

  ingredientTypes$?: Observable<IngredientType[]>;

  usedIngredientTypes = new Set<string>();
  newType = '0';
  isApproved = false;

  private formGroups: { [ingredientId: string]: UntypedFormGroup } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
    private readonly refDataService: RefDataService,
  ) {}

  ngOnInit(): void {
    this.ingredientTypes$ = this.refDataService.getByKey('ingredientTypes', false, this.domain, this.market, this.lang).pipe(
      map((ingredientTypes) =>
        this.refDataService.translateReference(this.refDataService.toArray(ingredientTypes), this.market, this.lang),
      ),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.ingredients?.currentValue && !this.formIngredients?.length) || (changes.formIngredients && this.ingredients)) {
      this.usedIngredientTypes = new Set<string>();
      const ingredients: IngredientLite[] = changes.ingredients?.currentValue ?? this.ingredients;

      if (this.formIngredients?.length) {
        this.formIngredients.controls = [];
        this.formIngredients.reset();
      }

      ingredients.forEach((ingredient) => {
        const formGroup = this.getFormGroup(ingredient);
        if (!ingredient.type) {
          formGroup.patchValue({ type: 'default' });
          this.usedIngredientTypes.add('default');
        } else {
          this.usedIngredientTypes.add(ingredient.type);
        }
        this.formIngredients?.push(formGroup);
      });
    }

    if (changes.isAutoControl) {
      this.isApproved = this.isAutoControl ? !this.isAutoControl.value : false;
    }
  }

  onDrop(event: CdkDragSortEvent<any>): void {
    if (event.previousIndex !== event.currentIndex && this.formIngredients && this.usedIngredientTypes) {
      // Change type order
      const tempArray = Array.from(this.usedIngredientTypes);
      // Remove initial element
      const [movedItem] = tempArray.splice(event.previousIndex, 1);
      // Insert element at new position
      tempArray.splice(event.currentIndex, 0, movedItem);

      this.usedIngredientTypes = new Set(tempArray);

      // Change ingredients order
      this.formIngredients.controls.sort(
        (firstIngredient, secondIngredient) =>
          tempArray.indexOf(firstIngredient.value.type) - tempArray.indexOf(secondIngredient.value.type),
      );
      // Signal change to form for update
      this.formIngredients.markAsDirty();
      this.formIngredients.updateValueAndValidity();
    }
  }

  addLine(newLine: { index?: number; applicationDescription?: string; type: string }): void {
    this.formIngredients?.insert(newLine?.index ?? this.formIngredients.length, this.getFormGroup(newLine));
    this.formIngredients?.controls.sort(
      (firstIngredient, secondIngredient) =>
        Array.from(this.usedIngredientTypes).indexOf(firstIngredient.value?.type) -
        Array.from(this.usedIngredientTypes).indexOf(secondIngredient.value?.type),
    );
    this.formIngredients?.markAsDirty();
  }

  parseIngredients(): void {
    this.removeEmptyLines();

    if (this.formIngredients) {
      this.recipeService
        .parseIngredients(this.formIngredients.value, {
          domain: this.domain,
          market: this.market,
          lang: this.lang,
        })
        .subscribe(
          (parsedIngredients: IngredientLite[]) => {
            this.formIngredients?.patchValue(parsedIngredients);
            this.formIngredients?.markAsDirty();
            this.notificationService.sendSuccess('RECIPES_EDIT.INGREDIENTS.PARSING_SUCCESS');
          },
          (err) => this.notificationService.sendAPIError(err),
        );
    }
  }

  newBlock(): void {
    this.newType = (+this.newType + 1).toString();
    if (this.usedIngredientTypes.size === 0) {
      // on first bloc
      this.newType = 'default';
      this.formIngredients?.push(this.getFormGroup({ type: this.newType }));
      this.formIngredients?.controls.forEach((control) => control.patchValue({ type: 'default' }));
      this.usedIngredientTypes = new Set<string>(['default']);
    } else {
      this.usedIngredientTypes.add(this.newType);
      this.formIngredients?.push(this.getFormGroup({ type: this.newType }));
    }
  }

  deleteBlock(type: string): void {
    this.usedIngredientTypes.delete(type);

    const length = this.formIngredients?.controls.length ?? 0;
    for (let i = length - 1; i >= 0; i--) {
      if (this.formIngredients?.controls[i].value.type === type) {
        this.formIngredients?.removeAt(i);
      }
    }

    if (this.usedIngredientTypes.size === 1) {
      this.formIngredients?.controls.forEach((control) => control.patchValue({ type: 'default' }));
      this.usedIngredientTypes = new Set<string>(['default']);
    }
    this.formIngredients?.markAsDirty();
  }

  getTypes(): string[] {
    return [...this.usedIngredientTypes];
  }

  selectIngredientType(data?: { oldType: string; newType: string }): void {
    const setTypes = this.getTypes();
    if (data) {
      setTypes[setTypes.indexOf(data.oldType)] = data.newType;
    }
    this.usedIngredientTypes = new Set(setTypes);
  }

  validatorType(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!this.ingredientTypes$) {
        return of(null);
      }
      return this.ingredientTypes$.pipe(
        map((types) => {
          if (
            types.some((ingredient) => ingredient.key === control.value) ||
            (control.value === 'default' && this.usedIngredientTypes.size === 1)
          ) {
            return null;
          } else {
            return { type: false };
          }
        }),
      );
    };
  }

  toggleApproved(): void {
    this.isAutoControl?.setValue(!this.isApproved);
    this.isAutoControl?.markAsDirty();
  }

  private removeEmptyLines(): void {
    let toRemove: number[] = [];

    this.formIngredients?.controls.forEach((control, index) => {
      const val = control.value;
      if (!val.applicationDescription && !val.quantity && !val.unit && !val.food && !val.requestedFood && !val.endFreeText) {
        toRemove = [index, ...toRemove]; // Add element at start of array to get descending order
      }
    });
    toRemove.forEach((index) => this.formIngredients?.removeAt(index));
  }

  private getFormGroup(ingredient?: Partial<IngredientLite>): UntypedFormGroup {
    if (ingredient?.identifier && this.formGroups[ingredient.identifier]?.pristine) {
      return this.formGroups[ingredient.identifier];
    }

    const marketingFoodsFormArray = ingredient?.suggestedMarketingFoods
      ? new UntypedFormArray(ingredient.suggestedMarketingFoods.map((marketingFood) => new UntypedFormControl(marketingFood)))
      : new UntypedFormArray([]);

    const endFreeTextControl = new UntypedFormControl(ingredient?.endFreeText);
    endFreeTextControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const value = endFreeTextControl.value;
      if (value) {
        // Capitalize the first letter
        endFreeTextControl.setValue(value.substr(0, 1).toUpperCase() + value.substr(1), { emitEvent: false });
      }
    });

    const formGroup = new UntypedFormGroup({
      identifier: new UntypedFormControl(ingredient?.identifier),
      applianceDescription: endFreeTextControl,
      applicationDescription: new UntypedFormControl(ingredient?.applicationDescription ?? ''),
      quantity: new UntypedFormControl(ingredient?.quantity, Validators.min(0)),
      unit: new UntypedFormControl(ingredient?.unit),
      endFreeText: endFreeTextControl,
      food: new UntypedFormControl(ingredient?.food),
      suggestedMarketingFoods: marketingFoodsFormArray,
      requestedFood: new UntypedFormControl(ingredient?.requestedFood),
      type: new UntypedFormControl(ingredient?.type, null, this.validatorType()),
      ingredientRecipe: new UntypedFormControl(ingredient?.ingredientRecipe),
    });

    if (ingredient?.identifier) {
      this.formGroups[ingredient.identifier] = formGroup;
    }

    return formGroup;
  }
}
